import React, { useEffect, useMemo } from 'react';

import { captureException } from '@sentry/browser';
import { useRouter } from 'next/router';
import { ErrorBoundary } from 'react-error-boundary';

import { getTeamInfo, Teams } from '@watershed/constants/teams';
import { atomLocale } from '@watershed/intl/frontend/atoms';
import deriveDefaultLocaleForFrontend from '@watershed/intl/frontend/deriveDefaultLocaleForFrontend';
import { useSyncAtom } from '@watershed/shared-frontend/components/jotai';
import useIdleLogout from '@watershed/shared-frontend/hooks/useIdleLogout';
import useSnackbar from '@watershed/shared-frontend/hooks/useSnackbar';
import {
  clearPinnedOrganizationId,
  getPinnedOrganizationId,
  initPinnedOrganizationId,
  switchPinnedOrganizationId,
} from '@watershed/shared-frontend/utils/pinnedOrganizationId';
import { useSearchParams } from '@watershed/shared-frontend/utils/queryParamHooks';
import { routeForOrgLogin } from '@watershed/shared-universal/dashboardRoutes';
import { ORG_ID_PARAM } from '@watershed/shared-universal/utils/pinnedOrganizationUniversalConstants';
import { configureSharedSentryContext } from '@watershed/shared-universal/utils/universalSentryUtils';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';
import { DialogContextProvider } from '@watershed/ui-core/components/Dialog';
import { PortalContextProviderWithNextRouter } from '@watershed/ui-core/utils/PortalContext';

import {
  GQActiveOrganizationFieldsFragment,
  GQFlagsInitFieldsFragment,
} from '@watershed/app-dashboard/generated/graphql-operations';

import AuthenticatedAnalyticsTracker from '../components/AuthenticatedAnalyticsTracker';
import { atomUserContext } from '../global-atoms/userAtoms';
import {
  determineCurrentProduct,
  useCurrentProductContext,
  WatershedProduct,
} from '../utils/CurrentProductContext';
import {
  FeatureFlagContextProvider,
  FeatureFlagMetadataMap,
  FeatureFlagsMap,
} from '../utils/FeatureFlag';
import { UserContextProvider, useUserContext } from '../utils/UserContext';
import { UserOnboardingsContextProvider } from '../utils/UserOnboardingsContext';
import { LoginAsCrossOrgWarning } from '../utils/useLoginAsCrossOrgWarning';
import useParsedUserContext from '../utils/useParsedUserContext';
import { ErrorFallback } from './ErrorFallback';
import { ViewerNoPermissionPage } from './NotFoundPage';
import { PrivilegeSyncer } from './PrivilegeSyncer';

function CrossOrgWarning() {
  const { orgId, orgName, loginAsUser } = useUserContext();
  return (
    <LoginAsCrossOrgWarning
      orgId={orgId}
      orgName={orgName}
      loginAsUserId={loginAsUser?.id}
    />
  );
}

type Key = 'ctrl' | 'cmd' | 'shift' | 'alt' | string;

export const useKeyboardShortcut = (keys: Array<Key>, callback: () => void) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        keys.every(
          (key) =>
            (key === 'ctrl' && event.ctrlKey) ||
            (key === 'cmd' && event.metaKey) ||
            (key === 'shift' && event.shiftKey) ||
            (key === 'alt' && event.altKey) ||
            (typeof key === 'string' &&
              event.key &&
              event.key.toLowerCase() === key)
        )
      ) {
        event.preventDefault();
        callback();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [keys, callback]);
};

declare global {
  interface Window {
    // window.wsDebug helps with debugging in the browser console.
    // It's typed as `unknown` to prevent any other code from relying on it.
    wsDebug: unknown;
  }
}

export default function SharedAppContainer({
  activeOrganization,
  flags: initialFlags,
  children,
  preferredLocale,
}: {
  activeOrganization: GQActiveOrganizationFieldsFragment;
  flags: Array<GQFlagsInitFieldsFragment>;
  preferredLocale: string | null;
  children: React.ReactNode;
}) {
  const [flags, flagMetadata]: [FeatureFlagsMap, FeatureFlagMetadataMap] =
    useMemo(() => {
      if (initialFlags === null) {
        return [new Map(), new Map()];
      }

      return [
        new Map(initialFlags.map((f) => [f.name, f.activated])),
        new Map(
          initialFlags.map((f) => [
            f.name,
            {
              description: f.description,
              featureTags: f.featureTags,
              team: f.team,
            },
          ])
        ),
      ];
    }, [initialFlags]);
  // This is where we would swap out the theme based on a feature flag, user/org
  // setting, etc.
  const derivedLocale = deriveDefaultLocaleForFrontend(preferredLocale);

  const snackbar = useSnackbar();
  const parsedUserContext = useParsedUserContext(
    activeOrganization,
    flags,
    preferredLocale
  );
  useSyncAtom(atomUserContext, parsedUserContext);
  useSyncAtom(atomLocale, derivedLocale);

  const userId = activeOrganization.user.id;
  const userName = activeOrganization.user.name;
  const loginAsUserId = activeOrganization.loginAsUser?.id;
  const loginAsUserName = activeOrganization.loginAsUser?.name;
  const isWatershedEmployee = activeOrganization.user.isWatershedEmployee;
  const isWatershedContractor = activeOrganization.user.isWatershedContractor;
  const isE2ETester = activeOrganization.user.isE2ETester;
  const orgId = activeOrganization.id;
  const orgName = activeOrganization.name;
  const testOrg = activeOrganization.testOrg;
  const demoOrg = activeOrganization.demoOrg;
  const sessionTimeoutMinutes = activeOrganization.sessionTimeoutMinutes;
  const referrer = document.referrer;

  const featureFlags: Record<string, boolean> = useMemo(() => {
    const myFlags: Record<string, boolean> = {};

    for (const [flag, isEnabled] of flags) {
      myFlags[flag] = isEnabled;
    }
    return myFlags;
  }, [flags]);

  // We need to pass in the feature flag value directly
  // because FeatureFlagContext is not available in this component
  // so we can't use the useFeatureFlag hook.
  useIdleLogout({
    sessionTimeoutMinutes,
  });

  // Set a CTRL+C command to copy the URL with the orgId appended
  const copyUrlWithOrg = () => {
    const url = new URL(window.location.href);
    url.searchParams.append(ORG_ID_PARAM, orgId);
    void navigator.clipboard.writeText(url.toString());
    snackbar.enqueueSnackbar('URL copied to clipboard');
  };
  // use two shortcuts to accomdate Mac and Windows users
  useKeyboardShortcut(['ctrl', 'shift', 'c'], copyUrlWithOrg);
  useKeyboardShortcut(['cmd', 'shift', 'c'], copyUrlWithOrg);

  // Get our organizationId from session storage in the case that we've switched accounts.
  const pinnedOrganizationId = getPinnedOrganizationId();

  // Get orgId from the URL in the case that we're using a cross-org-sharing link.
  const urlOrgId = useSearchParams().get(ORG_ID_PARAM);
  const targetOrg = urlOrgId
    ? parsedUserContext.accessibleOrgs.find((org) => org.id === urlOrgId)
    : null;

  const userCanAccessTargetOrgInCurrentSession =
    targetOrg && targetOrg.canAccessInCurrentSession;

  const redirectUrl = new URL(window.location.href);
  redirectUrl.searchParams.delete(ORG_ID_PARAM);
  const searchParamsString = redirectUrl.searchParams.toString();
  const redirectClean =
    searchParamsString.length > 0
      ? `${redirectUrl.pathname}?${searchParamsString}`
      : redirectUrl.pathname;

  if (
    targetOrg &&
    userCanAccessTargetOrgInCurrentSession &&
    targetOrg.id !== orgId
  ) {
    // if we have a valid cross-org-sharing link, switch to that org and redirect to the link path
    switchPinnedOrganizationId({
      orgId: targetOrg.id,
      shouldOpenInNewTab: false,
      redirect: redirectClean,
    });
  } else if (targetOrg && !userCanAccessTargetOrgInCurrentSession) {
    // redirect to the org-login for that org, with the redirect param set to the link path
    window.location.href = routeForOrgLogin(targetOrg.id, {
      redirect: redirectClean,
    });
  } else if (targetOrg && targetOrg.id === orgId) {
    // don't switch orgs but trim out the org id param in the url
    window.history.replaceState(window.history.state, '', redirectClean);
  } else if (pinnedOrganizationId === null) {
    initPinnedOrganizationId(orgId);
  } else if (pinnedOrganizationId !== orgId) {
    // This should never happen unless the data stored in sessionStorage
    // is malformed in some way. However, this is a scary thing to fix
    // so we'll be as defensive as possible. Reset the value in
    // session storage, warn and then force a refresh to clear the cache.
    console.warn(
      `ERROR: initOrganizationId was called with inconsistent ids - {orgId: ${orgId} vs ${pinnedOrganizationId}}`
    );
    captureException(
      new Error(
        `existing problem: initOrganizationId was called with inconsistent ids - {orgId: ${orgId} vs ${pinnedOrganizationId}}`
      ),
      {
        tags: {
          owner: getTeamInfo(Teams.EnterpriseFoundations).sentryTeam,
        },
      }
    );
    clearPinnedOrganizationId();
  }

  const { currentProduct, setCurrentProduct } = useCurrentProductContext();
  const { pathname } = useRouter();
  const { canAccessCorporate } = parsedUserContext;
  // We determine the current product in the SharedAppContainer
  // because we need user context to determine what to initially display,
  // but the CurrentProductProvider component lives higher up in the tree
  useEffect(() => {
    if (currentProduct === WatershedProduct.Unknown) {
      setCurrentProduct(determineCurrentProduct(pathname, canAccessCorporate));
    }
  }, [currentProduct, canAccessCorporate, pathname, setCurrentProduct]);

  useEffect(() => {
    configureSharedSentryContext({
      orgId,
      orgName,
      testOrg,
      demoOrg,
      userId,
      userName,
      loginAsUserId,
      loginAsUserName,
      isWatershedEmployee,
      isWatershedContractor,
      isE2ETester,
      referrer,
      featureFlags,
      sessionId: activeOrganization.sessionId,
    });
  }, [
    userId,
    userName,
    orgId,
    orgName,
    testOrg,
    demoOrg,
    loginAsUserId,
    loginAsUserName,
    isWatershedEmployee,
    isWatershedContractor,
    isE2ETester,
    referrer,
    featureFlags,
    activeOrganization.sessionId,
  ]);

  if (!window.wsDebug) {
    window.wsDebug = {
      activeOrganization,
      userContext: parsedUserContext,
      flags,
    };
  }

  if (urlOrgId && !targetOrg) {
    return <ViewerNoPermissionPage orgName={urlOrgId} />;
  }
  return (
    <UserContextProvider parsedUserContext={parsedUserContext}>
      <PrivilegeSyncer />
      <FeatureFlagContextProvider
        flags={flags}
        flagMetadata={flagMetadata}
        enableOverrides={isWatershedEmployee || isNotNullish(loginAsUserName)}
      >
        <AuthenticatedAnalyticsTracker>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <DialogContextProvider ErrorFallback={ErrorFallback}>
              <UserOnboardingsContextProvider>
                {/* <PortalContextProviderWithNextRouter> should be the inner-most, so it can
              take advantage of all other context providers. This provider just
              provides a place in the React tree to insert portals (modals,
              dialogs) into programatically. */}
                <PortalContextProviderWithNextRouter>
                  {children}
                  <CrossOrgWarning />
                </PortalContextProviderWithNextRouter>
              </UserOnboardingsContextProvider>
            </DialogContextProvider>
          </ErrorBoundary>
        </AuthenticatedAnalyticsTracker>
      </FeatureFlagContextProvider>
    </UserContextProvider>
  );
}
